import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { EmptyState } from '@routes-plan/ui';
import { ModalTable } from '../components/ReportLayout';
import { formatDate, formatMoney } from '../../inventory/shared/inventoryApi';
import { useT } from '../../../i18n';

type T = ReturnType<typeof useT>;

// Textos de los códigos de motivo y resolución que guarda el API (los códigos no cambian).
const reasonLabels = (t: T): Record<string, string> => ({
  DEFECTO_FABRICA: t('loanReports.productsHistory.reasonFactoryDefect'),
  GARANTIA: t('loanReports.productsHistory.reasonWarranty'),
  OTRO: t('loanReports.productsHistory.reasonOther'),
});
const resolutionLabels = (t: T): Record<string, string> => ({
  CAMBIO: t('loanReports.productsHistory.resolutionExchange'),
  DESCUENTO: t('loanReports.productsHistory.resolutionDiscount'),
});

/** Valor de una línea con la utilidad ya incluida (préstamos anteriores a 1.7.0 guardaban el % aparte). */
const lineValue = (item: any) =>
  Math.round(Number(item.subtotal ?? Number(item.unitPrice || 0) * Number(item.quantity || 0)) * (1 + Number(item.marginPercent || 0) / 100));

/**
 * Historial de productos de un préstamo (sedes de objeto real): lo entregado, lo devuelto y los
 * cambios. Se muestra en Información Cliente › Productos.
 */
const LoanProductsHistoryModal: React.FC<{ items: any[]; returns: any[] }> = ({ items, returns }) => {
  const t = useT();
  const REASONS = reasonLabels(t);
  const RESOLUTIONS = resolutionLabels(t);
  // Un producto puede venir en varias filas (una por lote): se agrupa por producto y por si fue cambio.
  const grouped = new Map<string, { title: string; sku: string; quantity: number; returned: number; value: number; replacement: boolean }>();
  for (const item of items) {
    const key = `${item.product?.id ?? item.productId}-${item.isReplacement ? 'r' : 'o'}`;
    const current = grouped.get(key) ?? {
      title: item.product?.title ?? t('loanReports.productsHistory.productFallback', { id: item.productId ?? '' }),
      sku: item.product?.sku ?? '',
      quantity: 0,
      returned: 0,
      value: 0,
      replacement: Boolean(item.isReplacement),
    };
    current.quantity += Number(item.quantity || 0);
    current.returned += Number(item.returnedQuantity || 0);
    current.value += lineValue(item);
    grouped.set(key, current);
  }
  const rows = Array.from(grouped.entries()).map(([key, g]) => ({
    id: key,
    product: g.sku ? `${g.title} (${g.sku})` : g.title,
    origin: g.replacement ? t('loanReports.productsHistory.deliveredByExchange') : t('loanReports.productsHistory.deliveredOnLoan'),
    quantity: g.quantity,
    returned: g.returned || '',
    unit: formatMoney(g.quantity ? g.value / g.quantity : 0),
    total: formatMoney(g.value),
  }));
  const total = Array.from(grouped.values())
    .filter((g) => !g.replacement)
    .reduce((acc, g) => acc + g.value, 0);
  const returnRows = returns.map((r) => ({
    id: r.id,
    date: formatDate(r.date),
    product: String(r.product?.title ?? ''),
    quantity: r.quantity,
    reason: REASONS[r.reason] ?? r.reason ?? '',
    resolution: RESOLUTIONS[r.resolution] ?? r.resolution ?? '',
    credit: Number(r.creditTotal || 0) > 0 ? formatMoney(r.creditTotal) : '',
    observation: String(r.observation ?? ''),
  }));

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            {t('loanReports.productsHistory.deliveredTitle')}
          </Typography>
          {rows.length > 0 && (
            <Typography variant="subtitle2">{t('loanReports.productsHistory.totalValue', { value: formatMoney(total) })}</Typography>
          )}
        </Box>
        {rows.length ? (
          <ModalTable
            rows={rows}
            getRowId={(r) => r.id}
            searchable={false}
            maxHeight="min(40vh, 360px)"
            exportFileName={t('loanReports.productsHistory.exportFile')}
            ariaLabel={t('loanReports.productsHistory.deliveredTitle')}
            columns={[
              { id: 'product', header: t('loanReports.productsHistory.product'), value: (r) => r.product },
              { id: 'origin', header: t('loanReports.productsHistory.origin'), value: (r) => r.origin, hideBelow: 'sm' },
              { id: 'quantity', header: t('loanReports.productsHistory.quantity'), value: (r) => r.quantity, align: 'right' },
              { id: 'returned', header: t('loanReports.productsHistory.returned'), value: (r) => r.returned, align: 'right' },
              { id: 'unit', header: t('loanReports.productsHistory.unitValue'), value: (r) => r.unit, align: 'right', hideBelow: 'sm' },
              { id: 'total', header: t('loanReports.value'), value: (r) => r.total, align: 'right' },
            ]}
          />
        ) : (
          <EmptyState dense title={t('loanReports.productsHistory.empty')} />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
          {t('loanReports.productsHistory.returnsTitle')}
        </Typography>
        {returnRows.length ? (
          <ModalTable
            rows={returnRows}
            getRowId={(r) => r.id}
            searchable={false}
            maxHeight="min(40vh, 360px)"
            ariaLabel={t('loanReports.productsHistory.returnsTitle')}
            columns={[
              { id: 'date', header: t('common.date'), value: (r) => r.date },
              { id: 'product', header: t('loanReports.productsHistory.product'), value: (r) => r.product },
              { id: 'quantity', header: t('loanReports.productsHistory.quantity'), value: (r) => r.quantity, align: 'right' },
              { id: 'reason', header: t('loanReports.productsHistory.reason'), value: (r) => r.reason },
              { id: 'resolution', header: t('loanReports.productsHistory.resolution'), value: (r) => r.resolution },
              { id: 'credit', header: t('loanReports.productsHistory.discount'), value: (r) => r.credit, align: 'right' },
              { id: 'observation', header: t('loanReports.productsHistory.observation'), value: (r) => r.observation, wrap: true },
            ]}
          />
        ) : (
          <EmptyState dense title={t('loanReports.productsHistory.noReturns')} />
        )}
      </Box>
    </Box>
  );
};

export default LoanProductsHistoryModal;
